import React, { useEffect, useMemo, useState } from "react";
import {
  CssBaseline,
  ThemeProvider,
  createTheme,
  Toolbar,
  Box,
  useMediaQuery,
} from "@mui/material";
import {
  Routes,
  Route,
  useLocation,
  Navigate,
  useNavigate,
  Outlet,
} from "react-router-dom";
import { useTranslation } from "react-i18next";

import "./i18n/i18n";

import { GoogleOAuthProvider } from "@react-oauth/google";
import { SOCIAL_CONFIG } from "./config";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import NewHeader from "./components/NewHeader";
import Footer from "./components/Footer";

import Home from "./pages/Home";
import LoginScreen from "./pages/LoginScreen";
import SignUpScreen from "./pages/SignUpScreen";
import NotFoundPage from "./pages/NotFoundPage";
import Profile from "./pages/Profile";
import LuckyWheel from "./pages/LuckyWheel";
import Rooms from "./pages/Rooms";
import BookRoomPage from "./pages/BookRoomPage";
import MyBookingsPage from "./pages/MyBookingsPage";
import BookingDetailsPage from "./pages/BookingDetailsPage";
import ProductsPage from "./pages/ProductsPage";
import ProductDetails from "./pages/ProductDetails";
import RewardsPage from "./pages/RewardsPage";
import MyOrdersPage from "./pages/MyOrdersPage";

export const ColorModeContext = React.createContext({
  toggleColorMode: () => {},
  setMode: () => {},
  mode: "light",
});

function getSystemMode() {
  if (typeof window === "undefined") return "light";
  return window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}


const AuthLayout = ({ user, isMobile }) => {
  // إذا لم يكن هناك مستخدم، توجه لصفحة اللوجن
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="App" style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      {/* الهيدر سيظهر في كل الصفحات المحمية تلقائياً */}
      <NewHeader />

      <main style={{ flex: 1 }}>
        {/* الـ Outlet هو المكان الذي ستظهر فيه الصفحات (Home, Profile, etc.) */}
        <Outlet />
        {/* {isMobile && <Box sx={{ height: "80px" }} />} */}
      </main>

      <Footer />
      {/* {isMobile && <Box sx={{ height: "60px" }} />} */}
    </div>
  );
};

function App() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));


  /**
   * mode can be:
   * - "light"
   * - "dark"
   * - "system" (follow device)
   */
  const [modePreference, setModePreference] = useState(() => {
    return localStorage.getItem("theme-mode") || "system";
  });

  // Actual applied mode (light/dark)
  const [mode, setMode] = useState(() => {
    const stored = localStorage.getItem("theme-mode") || "system";
    if (stored === "system") return getSystemMode();
    return stored;
  });


  // Language + direction persistence
  useEffect(() => {
    const storedLanguage = localStorage.getItem("language");
    if (storedLanguage) {
      i18n.changeLanguage(storedLanguage);
      document.documentElement.dir = storedLanguage === "ar" ? "rtl" : "ltr";
    } else {
      document.documentElement.dir = i18n.language === "ar" ? "rtl" : "ltr";
    }
  }, [i18n]);

  useEffect(() => {
    document.documentElement.dir = i18n.language === "ar" ? "rtl" : "ltr";
    localStorage.setItem("language", i18n.language);
  }, [i18n.language]);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Apply preference + follow device mode if "system"
  useEffect(() => {
    localStorage.setItem("theme-mode", modePreference);

    if (modePreference !== "system") {
      setMode(modePreference);
      return;
    }

    // If system mode: set initial + listen to changes
    setMode(getSystemMode());

    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setMode(mql.matches ? "dark" : "light");

    // addEventListener is modern; addListener is fallback
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else mql.addListener(onChange);

    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else mql.removeListener(onChange);
    };
  }, [modePreference]);

  const colorMode = useMemo(
    () => ({
      mode,
      setMode: (newPreference) => setModePreference(newPreference),
      toggleColorMode: () => {
        // toggle the *actual* mode and set preference accordingly
        setModePreference((prev) => {
          const currentApplied = prev === "system" ? getSystemMode() : prev;
          const next = currentApplied === "light" ? "dark" : "light";
          return next;
        });
      },
    }),
    [mode],
  );

  // ===== Brand Reds =====
  const BRAND_RED = "#800000";
  const BRAND_RED_DARK = "#FF0000"; 
  const BRAND_RED_HOVER = "#600000";
  const BRAND_BG_LIGHT = "#FFF5F5";
  const BRAND_BG_DARK = "#0F0505";

  const theme = useMemo(
    () =>
      createTheme({
        direction: i18n.language === "ar" ? "rtl" : "ltr",
        palette: {
          mode,
          primary: {
            main: mode === "light" ? BRAND_RED : BRAND_RED_DARK,
            contrastText: "#ffffff",
            brand: mode === "light" ? BRAND_RED : BRAND_RED_DARK,
            gradient:
              mode === "light"
                ? "linear-gradient(90deg, #FF4D4D 0%, #800000 55%, #600000 100%)"
                : "linear-gradient(90deg, #FF0000 0%, #CC0000 55%, #800000 100%)",
            borderColor: mode === "light" ? "#FFD1D1" : "#3A1A1A",
            softBackground: mode === "light" ? BRAND_BG_LIGHT : BRAND_BG_DARK,
            danger: mode === "light" ? "#800000" : "#FF0000",
            confirm: "#16A34A",
            header: mode === "light" ? "#8000001A" : "#FF00001A",
          },
          secondary: {
            main: mode === "light" ? "#111827" : "#E5E7EB",
            sec: mode === "light" ? "#F3F4F6" : "#1F2937",
          },
          text: {
            primary: mode === "light" ? "#0B0B0C" : "#F9FAFB",
            secondary: mode === "light" ? "#374151" : "#D1D5DB",
            main: "#ffffff",
          },
          background: {
            default: mode === "light" ? "#FFFFFF" : "#0B0B0C",
            paper: mode === "light" ? "#FFFFFF" : "#111315",
          },
          blackandWhite: {
            main: mode === "light" ? "#0B0B0C" : "#FFFFFF",
            main2: mode === "light" ? "#000" : "#fff",
          },
        },
        typography: {
          fontFamily:
            i18n.language === "ar"
              ? ["Cairo", "Almarai", "Poppins"].join(",")
              : ["Poppins", "Cairo", "Almarai"].join(","),
          h1: { fontWeight: 800 },
          h2: { fontWeight: 700 },
          h3: { fontWeight: 700 },
          h4: { fontWeight: 600 },
          h5: { fontWeight: 600 },
          h6: { fontWeight: 600 },
          button: { fontWeight: 600, textTransform: "none" },
        },
        shape: { borderRadius: 12 },
        components: {
          MuiCssBaseline: {
            styleOverrides: `
              body {
                scrollbar-color: ${
                  mode === "light"
                    ? `${BRAND_RED} #FFECEC`
                    : `${BRAND_RED_DARK} #111315`
                };
              }
              body::-webkit-scrollbar, *::-webkit-scrollbar {
                width: 10px;
                height: 10px;
              }
              body::-webkit-scrollbar-thumb, *::-webkit-scrollbar-thumb {
                border-radius: 10px;
                background: ${mode === "light" ? BRAND_RED : BRAND_RED_DARK};
              }
              body::-webkit-scrollbar-thumb:hover {
                background: ${mode === "light" ? BRAND_RED_HOVER : "#FF5A5A"};
              }
              body::-webkit-scrollbar-track, *::-webkit-scrollbar-track {
                border-radius: 10px;
                background: ${mode === "light" ? "#FFECEC" : "#111315"};
              }
            `,
          },
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: 12,
                fontWeight: 700,
                textTransform: "none",
                boxShadow: "none",
              },
              containedPrimary: {
                background:
                  mode === "light"
                    ? "linear-gradient(90deg, #FF4D4D 0%, #D10000 55%, #A80000 100%)"
                    : "linear-gradient(90deg, #FF6B6B 0%, #FF3B3B 55%, #D10000 100%)",
                color: "#fff",
                "&:hover": {
                  background: mode === "light" ? BRAND_RED_HOVER : "#FF5A5A",
                  boxShadow: "0px 10px 20px rgba(0,0,0,0.18)",
                },
              },
              outlinedPrimary: {
                borderColor: mode === "light" ? "#D10000" : "#FF3B3B",
                color: mode === "light" ? "#D10000" : "#FF3B3B",
                "&:hover": {
                  borderColor: mode === "light" ? BRAND_RED_HOVER : "#FF5A5A",
                  backgroundColor:
                    mode === "light"
                      ? "rgba(209, 0, 0, 0.06)"
                      : "rgba(255, 59, 59, 0.08)",
                },
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: 16,
                boxShadow:
                  mode === "light"
                    ? "0px 10px 30px rgba(0, 0, 0, 0.06)"
                    : "0px 12px 34px rgba(0, 0, 0, 0.35)",
                border:
                  mode === "light"
                    ? "1px solid rgba(0,0,0,0.06)"
                    : "1px solid rgba(255,255,255,0.06)",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                },
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: "none",
              },
            },
          },
        },
      }),
    [mode, i18n.language],
  );


  const { pathname } = useLocation();

  const hideHeader =
    pathname.includes("404") || pathname.includes("delete-account");
  const hideTransparentHeader =
    pathname.includes("home") ||
    pathname.includes("cars") ||
    pathname.includes("AboutUs");
  const hideFooter =
    pathname.includes("404") || pathname.includes("delete-account");
const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  return (
    <GoogleOAuthProvider clientId={SOCIAL_CONFIG.GOOGLE_CLIENT_ID}>
      <ColorModeContext.Provider value={colorMode}>
        <ThemeProvider theme={theme}>
          <CssBaseline />

          <div
            className="App"
            style={{
              display: "flex",
              flexDirection: "column",
              minHeight: "100vh",
            }}
          >
          <Routes>
  {/* المسارات العامة (لا تحتاج حماية) */}
  <Route path="/login" element={user ? <Navigate to="/home" replace /> : <LoginScreen />} />
  <Route path="/signup" element={user ? <Navigate to="/home" replace /> : <SignUpScreen />} />
  <Route path="/404" element={<NotFoundPage />} />

  {/* المسارات المحمية (كل ما بداخله محمي بـ AuthLayout) */}
  <Route element={<AuthLayout user={user} isMobile={isMobile} />}>
    <Route path="/home" element={<Home />} />
    <Route path="/profile" element={<Profile />} />
    <Route path="/Rooms" element={<Rooms />} />
    <Route path="/BookRoom/:id" element={<BookRoomPage />} />
    <Route path="/spin" element={<LuckyWheel />} />
    <Route path="/MyBookings" element={<MyBookingsPage />} />
    <Route path="/bookingdetails/:id" element={<BookingDetailsPage />} />
    <Route path="/Products" element={<ProductsPage />} />
    <Route path="/product/:id" element={<ProductDetails />} />
    <Route path="/Rewards" element={<RewardsPage />} />
    <Route path="/MyOrders" element={<MyOrdersPage />} />
    {/* أي صفحة جديدة تضيفها هنا ستكون محمية تلقائياً وبها هيدر وفوتر */}
  </Route>

  {/* توجيه المسار الرئيسي */}
  <Route path="/" element={<Navigate to="/home" replace />} />
  <Route path="*" element={<Navigate to="/404" replace />} />
</Routes>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={i18n.language === "ar"}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme={mode}
          />

        </ThemeProvider>
      </ColorModeContext.Provider>
    </GoogleOAuthProvider>
  );
}

export default App;
